import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  LinearProgress,
  InputBase,
  Tooltip,
} from '@mui/material';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import RouteIcon from '@mui/icons-material/Route';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  INTERVIEW_TRACKS,
  getTrackById,
  countTopics,
  ACCENT,
  PINK,
} from '../data/interviewPrepCurriculum';
import { getTopicArticle } from '../data/interviewPrep/content';
import TopicArticle from '../components/interview-prep/TopicArticle';
import MentorshipCallout from '../components/MentorshipCallout';
import { useCompletedTopics, useTrackProgress } from '../hooks/useInterviewPrepProgress';

const ICONS = {
  'system-design': ArchitectureOutlinedIcon,
  lld: AccountTreeOutlinedIcon,
  'cs-fundamentals': MemoryOutlinedIcon,
  behavioral: ForumOutlinedIcon,
};

/** Reading progress for one track — reads live from localStorage. Used on hub cards. */
const TrackProgressSummary = ({ trackId, totalTopics, accent, border, muted, isDark }) => {
  const { completed, total, percent } = useTrackProgress(trackId, totalTopics);
  if (completed === 0) return null;
  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: muted }}>
          {completed}/{total} completed
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: accent }}>
          {percent}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 5,
          borderRadius: 3,
          bgcolor: isDark ? alpha('#fff', 0.08) : alpha(accent, 0.12),
          '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

const InterviewPrepPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { trackId } = useParams();

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #e8f4f5 0%, #f7f9fb 35%, #ffffff 100%)';

  const track = useMemo(() => (trackId ? getTrackById(trackId) : null), [trackId]);

  if (trackId && !track) {
    return (
      <Box sx={{ minHeight: '70vh', bgcolor: pageBg, px: 3, py: 8, textAlign: 'center' }}>
        <Helmet>
          <title>Track not found | Interview Prep | CrafterHack</title>
        </Helmet>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Track not found</Typography>
        <Typography sx={{ color: muted, mb: 2 }}>
          That interview prep track doesn’t exist (yet).
        </Typography>
        <Button
          component={Link}
          to="/interview-prep"
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT }}
        >
          Back to Interview Prep
        </Button>
      </Box>
    );
  }

  if (track) {
    return (
      <TrackView
        track={track}
        isDark={isDark}
        border={border}
        surface={surface}
        muted={muted}
        pageBg={pageBg}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100%', bgcolor: pageBg, pb: 8 }}>
      <Helmet>
        <title>Interview Prep | System Design, LLD & More | CrafterHack</title>
        <meta
          name="description"
          content="Industry-standard interview prep: System Design (HLD), Low-Level Design, CS fundamentals, backend patterns, and behavioral interviews."
        />
        <link rel="canonical" href="https://crafterhack.com/interview-prep" />
      </Helmet>

      <Box
        sx={{
          borderBottom: `1px solid ${border}`,
          background: isDark
            ? `radial-gradient(ellipse 80% 60% at 20% -10%, ${alpha(ACCENT, 0.28)}, transparent 55%)`
            : `radial-gradient(ellipse 80% 60% at 20% -10%, ${alpha(ACCENT, 0.16)}, transparent 55%)`,
        }}
      >
        <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 4, md: 5.5 } }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1,
            }}
          >
            Interview prep curriculum
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.75rem', md: '2.15rem' },
              letterSpacing: '-0.035em',
              lineHeight: 1.15,
              mb: 1.25,
              maxWidth: 640,
            }}
          >
            Design systems. Model domains. Sound senior.
          </Typography>
          <Typography sx={{ color: muted, maxWidth: 560, lineHeight: 1.6, mb: 2.5 }}>
            Start from DSA Level 1 & 2, then move into High-Level Design, Low-Level Design,
            CS fundamentals, production backend, and behavioral stories — built for deliberate practice.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['DSA L1 & L2', 'HLD', 'LLD', 'CS fundamentals', 'Backend', 'Behavioral'].map(
              (label) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.08),
                    border: `1px solid ${border}`,
                  }}
                />
              ),
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 3.5 }}>
        <Box sx={{ mb: 3 }}>
          <MentorshipCallout variant="banner" />
        </Box>

        {/* DSA Level 1 & 2 — foundation before system design */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: isDark
              ? `linear-gradient(135deg, ${alpha(PINK, 0.1)}, transparent 55%)`
              : `linear-gradient(135deg, ${alpha(PINK, 0.06)}, transparent 55%)`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'flex-start' },
              justifyContent: 'space-between',
              gap: 2.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: PINK,
                  mb: 0.5,
                }}
              >
                Foundation first
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  letterSpacing: '-0.02em',
                  mb: 0.75,
                }}
              >
                DSA Level 1 & Level 2
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: muted, lineHeight: 1.6, maxWidth: 560, fontSize: '0.88rem' }}
              >
                System design and LLD land better when your DSA base is solid. Use Practice Track
                for Level 1 (fundamentals) and Level 2 (advanced patterns), or explore guided DSA
                programs if you want structured mentorship.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
                width: { xs: '100%', md: 420 },
                flexShrink: 0,
              }}
            >
              {[
                {
                  title: 'DSA Level 1',
                  body: 'Arrays, recursion, basics — build speed and clarity.',
                  to: '/practice-track?level=1',
                  cta: 'Open Practice Track',
                  icon: RouteIcon,
                  accent: ACCENT,
                },
                {
                  title: 'DSA Level 2',
                  body: 'Graphs, DP, harder patterns — interview depth.',
                  to: '/practice-track?level=2',
                  cta: 'Open Practice Track',
                  icon: RouteIcon,
                  accent: '#2563eb',
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Box
                    key={item.title}
                    component={Link}
                    to={item.to}
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      p: 1.75,
                      borderRadius: 2,
                      border: `1px solid ${border}`,
                      bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.85),
                      transition: 'border-color 0.2s, transform 0.2s',
                      '&:hover': {
                        borderColor: alpha(item.accent, 0.55),
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(item.accent, isDark ? 0.22 : 0.12),
                          color: item.accent,
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: muted, fontSize: '0.78rem', lineHeight: 1.45, mb: 1 }}
                    >
                      {item.body}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        color: item.accent,
                        fontWeight: 800,
                        fontSize: '0.78rem',
                      }}
                    >
                      {item.cta}
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${border}`,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <SchoolOutlinedIcon sx={{ fontSize: 18, color: muted }} />
              <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem' }}>
                Prefer guided DSA courses (Level 1 & Level 2 programs)?
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/programs"
              size="small"
              endIcon={<ArrowForwardIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              View DSA programs
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.75,
          }}
        >
          {INTERVIEW_TRACKS.map((item) => {
            const Icon = ICONS[item.id] || ArchitectureOutlinedIcon;
            const topicCount = countTopics(item);
            return (
              <Box
                key={item.id}
                component={Link}
                to={`/interview-prep/${item.id}`}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  p: 2.5,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  transition: 'border-color 0.2s, transform 0.2s',
                  '&:hover': {
                    borderColor: alpha(item.accent, 0.55),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(item.accent, isDark ? 0.22 : 0.12),
                      color: item.accent,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: item.accent,
                        mb: 0.35,
                      }}
                    >
                      {item.eyebrow}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: muted, fontWeight: 700, pt: 0.5 }}>
                    {topicCount} topics
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55, fontSize: '0.88rem' }}>
                  {item.summary}
                </Typography>
                {item.platforms?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                    {item.platforms.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: 'transparent',
                          border: `1px solid ${border}`,
                        }}
                      />
                    ))}
                  </Box>
                )}
                <TrackProgressSummary
                  trackId={item.id}
                  totalTopics={topicCount}
                  accent={item.accent}
                  border={border}
                  muted={muted}
                  isDark={isDark}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: item.accent, mt: 0.25 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>Explore track</Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            mt: 4,
            p: 2.5,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>How to use this curriculum</Typography>
          <Typography variant="body2" sx={{ color: muted, lineHeight: 1.6, maxWidth: 720 }}>
            Open a track and read it like a book — pick a topic from the table of contents, study
            diagrams and examples on the same page, then practice explaining it out loud or in Live
            Coding. Use the “Go deeper” links when you want the original papers and docs.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/** Flattens an article's blocks into plain text so search can match on body content, not just titles. */
const articleSearchText = (article) => {
  if (!article) return '';
  const parts = [article.title, article.subtitle];
  (article.blocks || []).forEach((b) => {
    if (b.text) parts.push(b.text);
    if (b.latex) parts.push(b.latex);
    if (b.meaning) parts.push(b.meaning);
    if (b.caption) parts.push(b.caption);
    if (b.code) parts.push(b.code);
    if (b.title) parts.push(b.title);
    if (Array.isArray(b.items)) {
      b.items.forEach((it) => {
        if (typeof it === 'string') parts.push(it);
        else parts.push(it.title, it.text, it.q, it.a);
      });
    }
    if (Array.isArray(b.headers)) parts.push(b.headers.join(' '));
    if (Array.isArray(b.rows)) parts.push(b.rows.flat().join(' '));
  });
  (article.qa || []).forEach((qa) => parts.push(qa.q, qa.a));
  return parts.filter(Boolean).join(' \n ').toLowerCase();
};

const TrackView = ({ track, isDark, border, surface, muted, pageBg }) => {
  const Icon = ICONS[track.id] || ArchitectureOutlinedIcon;
  const topicCount = countTopics(track);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [tocOpen, setTocOpen] = React.useState(false);
  const [query, setQuery] = useState('');
  const [focusMode, setFocusMode] = useState(false);
  const articleRef = useRef(null);
  const readerRef = useRef(null);
  const { completed, markComplete, toggle } = useCompletedTopics(track.id);
  const percent = topicCount > 0 ? Math.round((completed.size / topicCount) * 100) : 0;

  // Focus mode = real browser fullscreen on the reader shell (Navbar/Footer already
  // hide themselves whenever document.fullscreenElement is set — see App.js).
  useEffect(() => {
    const onFullscreenChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setFocusMode(Boolean(fsEl) && fsEl === readerRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  const toggleFocusMode = () => {
    const el = readerRef.current;
    if (!document.fullscreenElement) {
      const request =
        el?.requestFullscreen || el?.webkitRequestFullscreen || el?.msRequestFullscreen;
      request?.call(el)?.catch?.(() => {});
    } else {
      const exit =
        document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
      exit?.call(document)?.catch?.(() => {});
    }
  };

  const flatTopics = useMemo(
    () =>
      track.sections.flatMap((section) =>
        section.topics.map((topic) => ({ ...topic, sectionTitle: section.title, sectionId: section.id })),
      ),
    [track],
  );

  // Full-text index (title + subtitle + every block's text) built once per track — powers "search in the content".
  const searchIndex = useMemo(
    () =>
      flatTopics.map((topic) => ({
        id: topic.id,
        text: `${topic.label} ${articleSearchText(getTopicArticle(track.id, topic.id))}`.toLowerCase(),
      })),
    [flatTopics, track.id],
  );

  const matchIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(searchIndex.filter((entry) => entry.text.includes(q)).map((entry) => entry.id));
  }, [query, searchIndex]);

  const selectedId = searchParams.get('topic') || flatTopics[0]?.id;
  const selectedMeta = flatTopics.find((t) => t.id === selectedId) || flatTopics[0];
  const selectedIndex = flatTopics.findIndex((t) => t.id === selectedMeta?.id);
  const article = getTopicArticle(track.id, selectedMeta?.id);
  const isCurrentCompleted = selectedMeta ? completed.has(selectedMeta.id) : false;

  const selectTopic = (topicId) => {
    setSearchParams({ topic: topicId }, { replace: true });
    setTocOpen(false);
    requestAnimationFrame(() => {
      if (articleRef.current) {
        articleRef.current.scrollTop = 0;
      }
    });
  };

  useEffect(() => {
    if (!searchParams.get('topic') && flatTopics[0]?.id) {
      setSearchParams({ topic: flatTopics[0].id }, { replace: true });
    }
  }, [flatTopics, searchParams, setSearchParams]);

  const goPrev = () => {
    if (selectedIndex > 0) selectTopic(flatTopics[selectedIndex - 1].id);
  };
  const goNext = () => {
    // Reading past a chapter counts it as read.
    if (selectedMeta?.id) markComplete(selectedMeta.id, true);
    if (selectedIndex < flatTopics.length - 1) selectTopic(flatTopics[selectedIndex + 1].id);
  };

  const Toc = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${border}` }}>
        <Typography
          sx={{
            fontSize: '0.74rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: muted,
            mb: 0.35,
          }}
        >
          Table of contents
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.08rem', mb: 1 }}>{track.shortTitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: muted }}>
            {completed.size}/{topicCount} completed
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: track.accent }}>
            {percent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 5,
            borderRadius: 3,
            mb: 1.5,
            bgcolor: isDark ? alpha('#fff', 0.08) : alpha(track.accent, 0.12),
            '& .MuiLinearProgress-bar': { bgcolor: track.accent, borderRadius: 3 },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.1,
            py: { xs: 1, md: 0.6 },
            borderRadius: 1.5,
            border: `1px solid ${border}`,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          }}
        >
          <SearchIcon sx={{ fontSize: 17, color: muted, flexShrink: 0 }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search in this course…"
            inputProps={{ inputMode: 'search', enterKeyHint: 'search' }}
            sx={{ fontSize: '0.88rem', flex: 1, minWidth: 0, '& input': { p: 0 } }}
          />
          {query && (
            <IconButton
              size="small"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              sx={{ p: 0.5, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {matchIds && matchIds.size === 0 && (
          <Typography sx={{ px: 2, py: 2, fontSize: '0.88rem', color: muted, textAlign: 'center' }}>
            No topics match “{query.trim()}”.
          </Typography>
        )}
        {track.sections.map((section, sIdx) => {
          const visibleTopics = matchIds
            ? section.topics.filter((topic) => matchIds.has(topic.id))
            : section.topics;
          if (matchIds && visibleTopics.length === 0) return null;
          return (
          <Box key={section.id} sx={{ mb: 1.25 }}>
            <Typography
              sx={{
                px: 2,
                py: 0.75,
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: muted,
              }}
            >
              {String(sIdx + 1).padStart(2, '0')} · {section.title}
            </Typography>
            {visibleTopics.map((topic) => {
              const active = topic.id === selectedMeta?.id;
              const done = completed.has(topic.id);
              return (
                <Box
                  key={topic.id}
                  component="button"
                  type="button"
                  onClick={() => selectTopic(topic.id)}
                  sx={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.85,
                    width: '100%',
                    cursor: 'pointer',
                    px: 2,
                    py: 0.95,
                    pl: 2.5,
                    fontSize: '0.92rem',
                    fontWeight: active ? 800 : 600,
                    lineHeight: 1.4,
                    color: active ? track.accent : isDark ? '#c9d1d9' : '#2d3740',
                    bgcolor: active
                      ? alpha(track.accent, isDark ? 0.18 : 0.1)
                      : 'transparent',
                    borderLeft: `3px solid ${active ? track.accent : 'transparent'}`,
                    '&:hover': {
                      bgcolor: alpha(track.accent, isDark ? 0.12 : 0.06),
                    },
                  }}
                >
                  {done ? (
                    <CheckCircleIcon sx={{ fontSize: 17, color: track.accent, flexShrink: 0 }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: alpha(muted, 0.6), flexShrink: 0 }} />
                  )}
                  <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                    {topic.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={readerRef}
      sx={{
        bgcolor: pageBg,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        // Taller reader: nearly full viewport under the navbar. In focus mode
        // this element itself is the fullscreen root, so it owns the whole
        // screen and no longer needs to leave room for the navbar.
        height: { xs: 'auto', md: focusMode ? '100dvh' : 'calc(100dvh - 58px)' },
        minHeight: {
          xs: 'calc(100dvh - 56px)',
          md: focusMode ? '100dvh' : 'calc(100dvh - 58px)',
        },
        maxHeight: { md: focusMode ? '100dvh' : 'calc(100dvh - 58px)' },
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Helmet>
        <title>
          {selectedMeta?.label ? `${selectedMeta.label} · ` : ''}
          {track.title} | Interview Prep | CrafterHack
        </title>
        <meta name="description" content={article?.subtitle || track.summary} />
        {/* Canonicalize topic query variants (?topic=caching etc.) onto the
            base track page — they're chapters of one reading track, not
            standalone search-intent pages like the DSA topic listings. */}
        <link rel="canonical" href={`https://crafterhack.com/interview-prep/${track.id}`} />
      </Helmet>

      {/* Wide reader shell on desktop, like educative.io — nearly full viewport with a slim gutter at the outer edges.
          In focus mode the gutter/card chrome drops too, so the reader is truly edge-to-edge. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: focusMode ? '100%' : 1800,
          mx: 'auto',
          px: focusMode ? 0 : { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          // Minimal chrome so TOC + article get more vertical room
          pt: focusMode ? 0 : { xs: 1.5, md: 1 },
          pb: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: { md: focusMode ? 'none' : `1px solid ${border}` },
            borderRadius: { md: focusMode ? 0 : 2 },
            overflow: { md: 'hidden' },
            bgcolor: surface,
          }}
        >
          {/* Compact track header */}
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: `1px solid ${border}`,
              bgcolor: surface,
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                px: { xs: 0, md: 3 },
                py: { xs: 1.1, md: 1.1 },
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {isMobile && (
                <IconButton
                  size="small"
                  onClick={() => setTocOpen(true)}
                  aria-label="Open contents"
                  sx={{ border: `1px solid ${border}`, borderRadius: 1.5 }}
                >
                  <ListAltOutlinedIcon fontSize="small" />
                </IconButton>
              )}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(track.accent, isDark ? 0.22 : 0.12),
                  color: track.accent,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Breadcrumbs sx={{ '& a, & p': { fontSize: '0.75rem', color: muted } }}>
                  <MuiLink component={Link} to="/interview-prep" underline="hover">
                    Interview Prep
                  </MuiLink>
                  <Typography sx={{ fontWeight: 700, color: 'text.primary !important' }}>
                    {track.shortTitle}
                  </Typography>
                </Breadcrumbs>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedMeta?.label || track.title}
                </Typography>
              </Box>
              <Chip
                icon={<MenuBookOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                label={`${selectedIndex + 1} / ${topicCount}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  border: `1px solid ${border}`,
                  bgcolor: 'transparent',
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                label={`${percent}% complete`}
                size="small"
                sx={{
                  fontWeight: 800,
                  color: track.accent,
                  border: `1px solid ${alpha(track.accent, 0.4)}`,
                  bgcolor: alpha(track.accent, isDark ? 0.16 : 0.08),
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
              <Tooltip title={focusMode ? 'Exit focus mode' : 'Focus mode — fullscreen, no distractions'}>
                <IconButton
                  size="small"
                  onClick={toggleFocusMode}
                  aria-label="Toggle focus mode"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    border: `1px solid ${focusMode ? alpha(track.accent, 0.5) : border}`,
                    borderRadius: 1.5,
                    color: focusMode ? track.accent : muted,
                    bgcolor: focusMode ? alpha(track.accent, isDark ? 0.16 : 0.08) : 'transparent',
                  }}
                >
                  {focusMode ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 3,
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha(track.accent, 0.1),
              '& .MuiLinearProgress-bar': { bgcolor: track.accent },
            }}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
              gridTemplateRows: 'minmax(0, 1fr)',
            }}
          >
            {/* Desktop TOC — stays visible in focus mode too, just fullscreen with no navbar */}
            {!isMobile && (
              <Box
                sx={{
                  borderRight: `1px solid ${border}`,
                  bgcolor: surface,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {Toc}
              </Box>
            )}

            {/* Article pane */}
            <Box
              ref={articleRef}
              sx={{
                minHeight: 0,
                height: { md: '100%' },
                overflowY: { md: 'auto' },
                WebkitOverflowScrolling: 'touch',
                bgcolor: pageBg,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 1040,
                  mx: 'auto',
                  px: { xs: 2, sm: 3, md: 6 },
                  py: { xs: 2.5, md: 4 },
                  pb: { xs: 5, md: 7 },
                }}
              >
          {selectedMeta?.sectionTitle && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: track.accent,
                mb: 1,
              }}
            >
              {selectedMeta.sectionTitle}
            </Typography>
          )}

          <TopicArticle article={article} accent={track.accent} />

          {/* Mark as complete */}
          {selectedMeta?.id && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                onClick={() => toggle(selectedMeta.id)}
                startIcon={
                  isCurrentCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  )
                }
                variant={isCurrentCompleted ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  borderRadius: 5,
                  px: 2.5,
                  ...(isCurrentCompleted
                    ? { bgcolor: track.accent, '&:hover': { bgcolor: track.accent } }
                    : { color: muted, borderColor: border, '&:hover': { borderColor: track.accent, color: track.accent } }),
                }}
              >
                {isCurrentCompleted ? 'Completed' : 'Mark as completed'}
              </Button>
            </Box>
          )}

          {/* Chapter nav */}
          <Box
            sx={{
              mt: 2,
              pt: 2.5,
              borderTop: `1px solid ${border}`,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={goPrev}
              disabled={selectedIndex <= 0}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: muted,
                maxWidth: '46%',
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
                <Typography sx={{ fontSize: '0.65rem', color: muted }}>Previous</Typography>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedIndex > 0 ? flatTopics[selectedIndex - 1].label : '—'}
                </Typography>
              </Box>
            </Button>
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={goNext}
              disabled={selectedIndex >= flatTopics.length - 1}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: track.accent,
                maxWidth: '46%',
                justifyContent: 'flex-end',
              }}
            >
              <Box sx={{ overflow: 'hidden', textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.65rem', color: muted }}>Next</Typography>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedIndex < flatTopics.length - 1
                    ? flatTopics[selectedIndex + 1].label
                    : '—'}
                </Typography>
              </Box>
            </Button>
          </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={tocOpen}
        onClose={() => setTocOpen(false)}
        PaperProps={{
          sx: { width: 'min(320px, 88vw)', bgcolor: surface },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton size="small" onClick={() => setTocOpen(false)} aria-label="Close contents">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {Toc}
      </Drawer>
    </Box>
  );
};

export default InterviewPrepPage;
